import { PageContainer, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Popconfirm, Result, Tabs } from 'antd';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  acceptInvitation,
  queryUserInvitations,
  rejectInvitation,
} from '@/apps/auth/services/invitations';
import { authClient } from '@/common/utils/auth-client';

const MEMBER_ROLE = {
  owner: {
    text: 'Owner',
    status: 'Success',
  },
  admin: {
    text: 'Admin',
    status: 'Processing',
  },
  member: {
    text: 'Member',
    status: 'Default',
  },
};

const queryJoinedOrganizations = async () => {
  const { data, error } = await authClient.organization.list();
  if (error) {
    throw new Error(error.message || '获取组织列表失败');
  }
  return {
    data: Array.isArray(data) ? data : [],
    success: true,
    total: Array.isArray(data) ? data.length : 0,
  };
};

const OrganizationsPage = () => {
  const actionRef = useRef(null);
  const navigate = useNavigate();
  const { message } = App.useApp();

  const reload = () => {
    actionRef.current?.reload?.();
  };

  const { mutateAsync: accept, isPending: accepting } = useMutation({
    mutationFn: acceptInvitation,
    onSuccess: (result) => {
      const organizationId = result.data?.member?.organizationId;
      message.success('已加入组织');
      if (organizationId) {
        navigate(`/${encodeURIComponent(organizationId)}/account/organizations`, {
          replace: true,
        });
        return;
      }
      reload();
    },
    onError: () => {
      message.error('接受邀请失败，请重试');
    },
  });

  const { mutateAsync: reject, isPending: rejecting } = useMutation({
    mutationFn: rejectInvitation,
    onSuccess: () => {
      message.success('已拒绝邀请');
      reload();
    },
    onError: () => {
      message.error('拒绝邀请失败，请重试');
    },
  });

  const joinedColumns = [
    {
      title: '组织名称',
      dataIndex: 'name',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
    },
  ];

  const invitationColumns = [
    {
      title: '组织名称',
      dataIndex: ['organization', 'name'],
    },
    {
      title: '角色',
      dataIndex: 'role',
      valueEnum: MEMBER_ROLE,
    },
    {
      title: '过期时间',
      dataIndex: 'expiresAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => [
        <Button
          key="accept"
          type="link"
          loading={accepting}
          onClick={() => accept({ invitationId: record.id })}
        >
          接受
        </Button>,
        <Popconfirm
          key="reject"
          title="拒绝邀请"
          description={`确认拒绝加入 ${record.organization?.name}？`}
          okText="确认"
          cancelText="取消"
          onConfirm={() => reject({ invitationId: record.id })}
        >
          <Button type="link" danger loading={rejecting}>
            拒绝
          </Button>
        </Popconfirm>,
      ],
    },
  ];

  return (
    <PageContainer>
      <Tabs
        items={[
          {
            key: 'joined',
            label: '已加入组织',
            children: (
              <ProTable
                rowKey="id"
                headerTitle="已加入组织"
                request={queryJoinedOrganizations}
                columns={joinedColumns}
                search={{
                  labelWidth: 96,
                }}
              />
            ),
          },
          {
            key: 'pending',
            label: '待加入组织',
            children: (
              <ProTable
                actionRef={actionRef}
                rowKey="id"
                headerTitle="待加入组织"
                request={queryUserInvitations}
                columns={invitationColumns}
                search={{
                  labelWidth: 96,
                }}
                locale={{
                  emptyText: (
                    <Result
                      status="info"
                      title="暂无待处理邀请"
                      subTitle="当前账号没有需要确认的组织邀请。"
                    />
                  ),
                }}
              />
            ),
          },
        ]}
      />
    </PageContainer>
  );
};

export default OrganizationsPage;
